import React, {useEffect, useState, useRef, useMemo} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'

import {Box, VStack, useMultiStyleConfig, useDisclosure, StackProps, HStack, Text, TextProps, Divider, Flex, Stack} from '@chakra-ui/react'
import {
    useViewport,
    useAnalytics,
    useHeaderSizeWithoutBanner,
    useBasket,
    useWishlist,
    useWishlistActions,
    useScrollAtTop,
    useIsElementInViewport,
    STICKY_SECTION_MARKER_CLASS,
} from '_customHooks'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useEinstein from '_customHooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

import {useDrawer, useGlobalConfigState, useLoader, useModal, useHeaderState, useHeaderDispatch} from '_context'
import {capitalize, computeMediaItems, mapProductDetailsPageContent, resolveHostLink, getAbsoluteUrl, MediaItem} from '_utils'
import {Button, Icon, IconButton, Tag, ShareBox, Loader, Breadcrumb, TextItem, ImageItem, Price} from '_uiComponents'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {analyticsEventAddToCart} from '_analytics/events'
import ProductSizeSelector from './partials/size-selector'
import ProductRequestSize from './partials/request-size'
import ProductColorSelector from './partials/color-selector'
import ProductColorSwatches from './partials/color-swatches'
import ProductMediaCarousel, {ProductMediaCarouselHandle} from './partials/media-carousel'
import ZoomImageModal from './partials/media-carousel/partials/zoom-image-modal'
import {FullProductType, Page, ProductVariant, ProductVariationGroup, BannerPageRef} from '_types'
import Stickybar from './partials/stickybar'
import ProductFindInBoutique from './partials/find-in-boutique'
import FindInBoutiqueTitle from './partials/find-in-boutique/partials/find-in-boutique-title'
import ContactUs from './partials/contact-us'
import ContactUsTitle from './partials/contact-us/partials/contact-us-title'
import ProductAttributeSelectorTitle from './partials/size-selector/partials/size-selector-title'
import ProductRecommendations from './partials/recommendations'
import StickyAddToCart from './partials/sticky-add-to-cart'
import SizeGuide from './partials/size-guide'
import ZoomModalHeader from './partials/media-carousel/partials/zoom-image-modal/partials/zoom-modal-header'
import ProductInfo from './partials/product-info'
import RelatedProductsSelector from './partials/related-products-selector'
import {BannerPage} from './partials/banner-page'

// Origin of an add-to-cart action; drives the analytics `dimension22`. The `_zoom` variants are emitted
// when the add-to-cart is performed from a sticky bar while the PDP zoom modal is open (desktop sticky bar
// and mobile confirm layer); see the suffixing in handleAddToCart.
export type AddToCartOrigin = 'pdp' | 'sticky_bar' | 'confirm_layer_add_to_cart' | 'sticky_bar_zoom' | 'confirm_layer_add_to_cart_zoom'

interface ProductDetailsProps {
    product: FullProductType
    onProductChange: (id: string) => void
    onVariantSelected: () => void
    pageDesignerData?: Page
    bannerPageRefs?: BannerPageRef[]
}

const ProductDetails = React.memo(({product, onProductChange, onVariantSelected, pageDesignerData, bannerPageRefs}: ProductDetailsProps) => {
    const addToCartBtnRef = useRef<HTMLButtonElement>(null)
    const stickybarRef = useRef<HTMLDivElement>(null)
    // Page-owned "the white band has started leaving the viewport" signal (mobile only). Drives WHICH
    // element carries the single `cc-sticky-section` marker: while false (page at scroll-top) the band is
    // the active section; once true (page scrolled, so on mobile the band has started moving up) the fixed
    // CTA is. Pure scroll read — it does NOT change the `mobileCtaMode` state machine.
    const [isBandLeaving, setIsBandLeaving] = useState(false)
    // Marks the start of the recommendations section: when it enters the viewport the mobile "BUY" CTA
    // is hidden so it never overlaps the recommendations (mobile only; desktop is unaffected).

    const headerSize = useHeaderSizeWithoutBanner()
    const headerSizeCollapsed = useHeaderSizeWithoutBanner(true)
    const {
        analyticsEventInit,
        analyticsProductSizeSelectorOpenClose,
        analyticsProductSizeSelectorSizeSelect,
        analyticsProductFindInBoutiqueSizeSelection,
        analyticsEventProductDetail,
        analyticsEventSizeChartClick,
        analyticsEventPdpDetailLayer,
        analyticsEventPdpColorLayer,
        analyticsEventPdpColorLayerSelect,
        analyticsProductRelatedClick,
        analyticsEventExclusiveServicesClick,
    } = useAnalytics()
    const {data: customer} = useCurrentCustomer()
    const {basket, addItemToBasket} = useBasket(true)
    const {isProductInWishlist, refetchWishlists} = useWishlist()
    const {addItemToWishlist, isActionLoading, removeItemFromWishlist} = useWishlistActions()
    const {appPreferences} = useGlobalConfigState()
    const {showLoader, hideLoader} = useLoader()
    const {openModal, closeModal, updateModalTitle, updateModalBody, updateModalFooter} = useModal()
    const [isZoomModalOpen, setIsZoomModalOpen] = useState(false)
    const [mobileCtaMode, setMobileCtaMode] = useState<'inflow' | 'fixed' | 'hidden'>('inflow')
    const {formatMessage} = useIntl()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const {locale, site} = useMultiSite()
    const {isTablet, isDesktop} = useViewport()
    const isAtTop = useScrollAtTop()
    const {setHeaderExpanded} = useHeaderDispatch()
    // Ref-mirror so the sticky IntersectionObserver callback (bound once in initializeSticky) always
    // reads the latest scroll-at-top value instead of the one captured when the observer was created.
    const isAtTopRef = useRef(isAtTop)
    isAtTopRef.current = isAtTop
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const {openDrawer, closeDrawer, updateClosingIcon, isOpen: isDrawerOpen, updateDrawerTitle} = useDrawer()
    // Mobile menu (Sidebar) open state — used to hide the sticky BUY CTA while the menu is open, the
    // same way it's already hidden for the zoom modal. Avoids touching z-indexes: the button sits above
    // the menu overlay otherwise. It is remounted automatically when the menu closes.
    const {isSidebarOpen, isHeaderExpanded} = useHeaderState()
    const location = useLocation()

    const LABEL_SELECT_SIZE = formatMessage({id: 'label.select.size', defaultMessage: 'Select your size'})
    const LABEL_SIZE_SELECTOR_TITLE = formatMessage({id: 'label.requestsize.title', defaultMessage: 'Select your size'})
    const LABEL_SELECTED_SIZE = formatMessage({id: 'product.label.size', defaultMessage: 'Size:'})
    // The main PDP CTA copy is a single shared key (buy.button, "ADD TO CART") across desktop, mobile and
    // tablet. The breakpoints differ only in behaviour (desktop adds straight to cart; mobile/tablet open a
    // confirm popup / size selector), not in label. The sticky bars are unaffected.
    const LABEL_ADD_TO_CART = formatMessage({id: 'buy.button', defaultMessage: 'ADD TO CART'})
    const LABEL_FIND_IN_BOUTIQUE = formatMessage({id: 'label.find.in.boutique', defaultMessage: 'FIND IN BOUTIQUE'})?.toUpperCase()
    const LABEL_CONTACT_US = formatMessage({id: 'label.contact.explain', defaultMessage: 'MAY WE HELP YOU?'})?.toUpperCase()
    const LABEL_SELECT_COLOR = formatMessage({id: 'label.requestcolor.title', defaultMessage: 'Select your color'})
    const LABEL_PRODUCT_DETAILS = capitalize(formatMessage({id: 'label.details', defaultMessage: 'DETAILS'}))
    const LABEL_PRODUCT_PACKAGING = capitalize(formatMessage({id: 'label.packaging', defaultMessage: 'PACKAGING'}))
    const LABEL_PRODUCT_SHIPPING_RETURNS = capitalize(formatMessage({id: 'label.shipping.returns', defaultMessage: 'SHIPPING AND RETURNS'}))

    const styles = useMultiStyleConfig('ProductDetails', {headerSize, headerSizeCollapsed, isHeaderExpanded}) as Record<string, StackProps & TextProps>
    const {isOpen: isShareProductPopoverOpen, onToggle: onToggleShareProductPopover, onClose: onCloseShareProductPopover} = useDisclosure()
    const {
        activeVariation,
        productFinalPrice,
        productPriceNumber,
        productTextTag,
    }: {activeVariation: ProductVariationGroup; productFinalPrice: string; productPriceNumber: number; productTextTag: string} = useMemo(
        () => getProductAnalyticsInfo(product),
        [product],
    )

    const pageContent = useMemo(() => mapProductDetailsPageContent(pageDesignerData), [pageDesignerData])

    // Shipping disclaimer messages, each gated by its own product-level flag.
    // Match each message by its authored Page Designer component `name` rather than by array index:
    // campaigns can hide components (e.g. the PFAS ones), so the PFAS and CITES messages aren't
    // guaranteed to sit at fixed positions in `pageContent['shipping']`. A positional lookup would
    // mispair the surviving message with the wrong flag (showing the CITES text on a PFAS product).
    const shippingMessages = useMemo(() => {
        const components = pageContent?.['shipping'] ?? []
        const byName = (...names: string[]) => components.find((component) => names.includes(component?.name))

        return [
            // PFAS disclaimer — authored as "PFAS EU" or "PFAS" depending on the market.
            {content: byName('PFAS EU', 'PFAS'), show: activeVariation.pfas},
            // CITES / shipment disclaimer.
            {content: byName('CITES'), show: activeVariation.enableDisclaimerShipment},
        ].filter((message) => message.show && message.content && (message.content.text || message.content.links))
    }, [pageContent, activeVariation])

    const shouldAddToCartAfterSizeSelection = useRef<boolean>(false)
    const shouldAddToCartAfterSizeSelectionSticky = useRef<boolean>(false)
    // Tracks whether the size-selector drawer is being closed because a size was selected
    // (vs. dismissed by the user), so we don't emit a spurious 'close' analytics event.
    const sizeSelectedRef = useRef<boolean>(false)
    const bannerAndAppointmentRef = useRef<HTMLDivElement>(null)
    // Holds the activeMediaItems reference the zoom modal was opened with, so the body-refresh effect
    // below can tell an initial open (preserve the clicked slide) from a later color/variation change
    // while the zoom is open (swap the gallery, restart at the first image).
    const zoomOpenedMediaItemsRef = useRef<MediaItem[] | null>(null)

    const getPreselectedVariant = (): ProductVariant | null => {
        const preselectedVariant = product?.c_selectedVariantID ? activeVariation?.variants?.find((v) => v.ID === product.c_selectedVariantID) : null
        const preselectedVariantAvailable = preselectedVariant && preselectedVariant.online && preselectedVariant.availability?.inStock

        const singleVariant = activeVariation?.variants?.length === 1 ? activeVariation?.variants[0] : null
        const singleVariantAvailable = singleVariant && singleVariant.online && singleVariant.availability?.inStock

        return preselectedVariant && preselectedVariantAvailable
            ? (preselectedVariant as unknown as ProductVariant)
            : singleVariant && singleVariantAvailable
              ? singleVariant
              : null
    }

    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(getPreselectedVariant())

    // Desktop image scroll-gating. While the gate is CLOSED the images+details section is pinned to
    // the top of the viewport and page scroll is blocked; a wheel listener bound to the pinned
    // section drives the vertical media carousel (via its imperative handle) so scrolling ANYWHERE
    // over the pinned section advances the images — not only over the gallery itself. The gate opens
    // once every image has been seen, and re-arms when the user scrolls the page back to the very top.
    // Mobile/tablet are unaffected (the gate only exists on desktop).
    //
    // IMPORTANT: the listener is bound to the section element (not `window`), and preventDefault only
    // runs for wheel events originating inside that element. Modals, drawers and the zoom overlay are
    // portaled to `document.body` (outside this element), so their wheel events are never intercepted
    // and they scroll natively.
    const [isImagesGateOpen, setIsImagesGateOpen] = useState(false)
    // Any full-screen overlay on top of the PDP (zoom modal, any drawer) must fully suspend the gate:
    // the section unpins so it can't trap the overlay's scroll, and the wheel listener bails out.
    const isOverlayOpen = isZoomModalOpen || isDrawerOpen
    // The pinned/gated state ONLY applies while the page is physically at the very top. This is critical
    // for correctness: pinning the section (position:sticky + capped 100vh height + overflow:hidden)
    // while the page is scrolled down collapses the section height and pulls the content below it
    // (recommendations carousel) up over the gallery. That happens on a color change made while scrolled,
    // and on browser Back (native scroll restoration lands at a non-zero offset on a fresh mount). Gating
    // only at the top keeps the section in normal flow in those cases, so the layout never breaks.
    const isGateActive = isDesktop && !isImagesGateOpen && !isOverlayOpen && isAtTop
    // Ref-mirrors so the (bound-once) wheel/scroll listeners always read the latest values
    // without re-binding.
    const isGateActiveRef = useRef(isGateActive)
    isGateActiveRef.current = isGateActive
    const isImagesGateOpenRef = useRef(isImagesGateOpen)
    isImagesGateOpenRef.current = isImagesGateOpen
    const mediaCarouselRef = useRef<ProductMediaCarouselHandle>(null)
    // The pinned images+details section element; the wheel listener is scoped to it.
    const imagesDetailsRef = useRef<HTMLDivElement>(null)

    // Feeds the Price component: selectedVariant's pricing (a specific size) takes priority over the color variation's
    const {priceValue, discountedPrice, discountedPriceMinRange, priceMinRange} = useMemo(() => {
        const pricing = selectedVariant?.pricing ?? activeVariation?.pricing
        const hasDiscount = Boolean(pricing?.hasDiscount)
        const hasPriceRange = Boolean(pricing?.hasPriceRange)

        return {
            priceValue: pricing?.price?.formattedValue,
            discountedPrice: hasDiscount && !hasPriceRange ? pricing?.discount?.price?.formattedValue : undefined,
            discountedPriceMinRange: hasDiscount && hasPriceRange ? pricing?.discount?.priceRange?.minPrice?.formattedValue : undefined,
            priceMinRange: hasPriceRange ? pricing?.priceRange?.minPrice?.formattedValue : undefined,
        }
    }, [selectedVariant, activeVariation])

    // Computes the ordered list of media items for the active variation.
    // Shared utility so carousel, zoom body and zoom modal.
    const activeMediaItems = useMemo(
        () => computeMediaItems({images: activeVariation?.images, videos: activeVariation?.videos, isDesktop, isTablet, type: 'pdp'}),
        [activeVariation],
    )
    const breadcrumbItems = useMemo(() => {
        const itemListElement = (product?.c_seo?.schemaBreadcrumbs as {itemListElement?: Array<{item?: string; name?: string}>} | undefined)?.itemListElement

        if (!itemListElement || !Array.isArray(itemListElement)) {
            return []
        }

        // The last breadcrumb item is the leaf product name. Per UX design (Figma) and SEO
        // confirmation, it is removed from the visible breadcrumb while remaining in the
        // structured data (ld+json), which is rendered independently from `schemaBreadcrumbs`.
        return itemListElement
            .filter((item) => item?.name)
            .slice(0, -1)
            .map((item, index) => ({
                id: String(index),
                htmlValue: item.name || '',
                url: item.item || '',
            }))
    }, [product])

    const selectedWishlistVariant = selectedVariant?.ID ?? activeVariation?.ID ?? product?.id
    const isInWishlist = isProductInWishlist(product.id, selectedWishlistVariant)
    const shareHost = resolveHostLink(appPreferences?.hostLink, site?.id)
    const shareUrl =
        shareHost && activeVariation?.pdpUrl
            ? getAbsoluteUrl(shareHost, locale.alias, activeVariation?.pdpUrl.replace(`/${locale.alias}`, ''))
            : activeVariation?.pdpUrl

    const addToWishlistAriaLabel = formatMessage(
        {
            id: 'button.common.add.to.wishlist',
            defaultMessage: 'Save in Wishlist {0}',
        },
        {
            0: product?.c_shortDescription || product?.shortDescription || '',
        },
    )

    const handleAddToWishlist = async () => {
        if (isInWishlist) {
            await removeItemFromWishlist(selectedWishlistVariant, product)
        } else {
            await addItemToWishlist(product, selectedWishlistVariant)

            // Dynamic Yield add to wishlist Tracking (same behaviour as PLP tile)
            if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                let prop: {dyType: string; productId: string; size?: string} = {
                    dyType: 'add-to-wishlist-v1',
                    productId: product.id,
                }

                if (selectedVariant?.ID) {
                    prop = {
                        ...prop,
                        productId: selectedVariant.ID,
                        size: selectedVariant.size?.label,
                    }
                }

                try {
                    window.DY.API('event', {
                        name: 'Add to Wishlist',
                        properties: prop,
                    })
                } catch (error) {
                    console.error('[DY Tracking] Error on add to wishlist event:', error)
                }
            }
        }

        void refetchWishlists()
    }

    // ZoomModalHeader is a stable external component so updateModalTitle triggers
    // an in-place prop update (no remount) — internal share-popover state is preserved.
    useEffect(() => {
        if (!isZoomModalOpen) return
        updateModalTitle(
            <ZoomModalHeader
                isActionLoading={isActionLoading}
                isInWishlist={isInWishlist}
                addToWishlistAriaLabel={addToWishlistAriaLabel}
                onAddToWishlist={handleAddToWishlist}
                shareUrl={shareUrl}
                shareSubject={formatMessage({id: 'label.wishlist.share.email.subject', defaultMessage: 'I would like to share this product with you'})}
            />,
        )
        // selectedWishlistVariant must be a dependency: the zoom header is a snapshot pushed into
        // ModalContext, so its onAddToWishlist closure only refreshes when this effect re-runs. Without
        // it the header keeps a stale selectedWishlistVariant and removeItemFromWishlist can't resolve the
        // wishlist item IDs to delete — the product gets added but never removed (the toast still fires).
    }, [isZoomModalOpen, isInWishlist, isActionLoading, selectedWishlistVariant])

    // Swap the zoom body only when the media set changes (a color/variation change) WHILE the zoom is
    // already open — then the new gallery starts at the first image. On the initial open we must NOT run
    // this: handleMediaSlideClick already mounted ZoomImageModal at the clicked slide, and overwriting it
    // here with initialIndex={0} would always snap the zoom back to the first photo (the reported bug).
    useEffect(() => {
        if (!isZoomModalOpen) return
        // First run for this open: openModal set the body at the clicked index; just record the media set.
        if (zoomOpenedMediaItemsRef.current === activeMediaItems) return
        zoomOpenedMediaItemsRef.current = activeMediaItems
        updateModalBody(
            <ZoomImageModal key={activeVariation?.ID} mediaItems={activeMediaItems} initialIndex={0} productName={product?.c_shortDescription ?? ''} />,
        )
    }, [isZoomModalOpen, activeMediaItems])

    // On mobile the buy CTA is rendered inside the modal itself (as its footer) rather than as a
    // page-level fixed button raised above the modal's z-index — that approach would also end up
    // on top of any drawer opened afterwards (e.g. size selector), since drawers and the modal share
    // the same stacking level.
    useEffect(() => {
        // Mobile and tablet render the BUY CTA inside the zoom modal as its footer (tablet reuses the
        // mobile confirmation-layer CTA — Figma node 60805-38152). Desktop uses the sticky bar instead.
        if (!isZoomModalOpen || isDesktop) return
        updateModalFooter(
            <StickyAddToCart
                activeVariation={activeVariation}
                selectedVariant={selectedVariant}
                productName={product?.shortDescription}
                productPrice={productFinalPrice}
                productPriceNumber={productPriceNumber}
                onAddToCart={(origin) => handleAddToCart(origin)}
            />,
        )
    }, [isZoomModalOpen, isDesktop, selectedVariant])

    const handleMediaSlideClick = (selectedIndex: number) => {
        onCloseShareProductPopover()
        setIsZoomModalOpen(true)
        document.body.style.overflow = 'hidden'
        // Record the media set this open started with so the body-refresh effect skips its first run and
        // preserves the clicked slide (it only swaps the gallery on a later color/variation change).
        zoomOpenedMediaItemsRef.current = activeMediaItems
        openModal({
            title: (
                <ZoomModalHeader
                    isActionLoading={isActionLoading}
                    isInWishlist={isInWishlist}
                    addToWishlistAriaLabel={addToWishlistAriaLabel}
                    onAddToWishlist={handleAddToWishlist}
                    shareUrl={shareUrl}
                    shareSubject={formatMessage({id: 'label.wishlist.share.email.subject', defaultMessage: 'I would like to share this product with you'})}
                />
            ),
            body: <ZoomImageModal mediaItems={activeMediaItems} initialIndex={selectedIndex} productName={product?.c_shortDescription ?? ''} />,
            footer: !isDesktop ? (
                <StickyAddToCart
                    activeVariation={activeVariation}
                    selectedVariant={selectedVariant}
                    productName={product?.shortDescription}
                    productPrice={productFinalPrice}
                    productPriceNumber={productPriceNumber}
                    onAddToCart={(origin) => handleAddToCart(origin)}
                />
            ) : undefined,
            variant: 'mediaZoom',
            className: 'cc-zoom-modal',
            closingIcon: 'IconCloseLight',
            // Figma (node 5655-4320): PDP modal/drawer close (X) and back (<) icons are 18px.
            closingIconSize: 'sm-plus',
            onClose: () => {
                setIsZoomModalOpen(false)
                document.body.style.overflow = ''
                // Clear so the next open re-primes (and its effect skips the first run again).
                zoomOpenedMediaItemsRef.current = null
                // The sticky bar exists on desktop only; toggle it there.
                if (isDesktop) {
                    stickybarRef.current?.classList.add('hidden')
                }
            },
        })
        if (isDesktop) {
            stickybarRef.current?.classList.remove('hidden')
        }
    }

    // Origin of an add-to-cart, mapped 1:1 to the analytics `dimension22` (via getProductImpressionAnalytics `page`):
    // - 'pdp': main PDP buy button
    // - 'sticky_bar': desktop sticky bar
    // - 'confirm_layer_add_to_cart': mobile confirmation layer (manuale BitBang PDP PWA §3.1.6)
    const handleAddToCart = (origin: AddToCartOrigin = 'pdp') => {
        if (!selectedVariant) {
            if (!shouldAddToCartAfterSizeSelectionSticky.current) {
                openSizeSelector(true)

                // Desktop-only: the inline PDP "ADD TO CART" button auto-adds after a size is picked via
                // this ref. Mobile and tablet use the StickyAddToCart confirmation layer, which manages its
                // own "open popover after size selection" flow, so this ref must stay false there.
                if (isDesktop) {
                    shouldAddToCartAfterSizeSelection.current = true
                }
            }
            return
        }

        showLoader()

        // Resolve the analytics `dimension22` from the add-to-cart origin and the zoom-modal state:
        // - 'pdp': main PDP buy button (never shown inside the zoom modal, so never suffixed)
        // - desktop sticky bar: 'sticky_bar', or 'sticky_bar_zoom' when the zoom modal is open
        // - mobile confirmation layer: reported as the PDP — 'pdp' normally, 'pdp_zoom' when the add-to-cart
        //   is triggered from the confirmation layer opened with the zoom modal open
        const analyticsDimension22: 'pdp' | 'pdp_zoom' | 'sticky_bar' | 'sticky_bar_zoom' =
            origin === 'confirm_layer_add_to_cart'
                ? isZoomModalOpen
                    ? 'pdp_zoom'
                    : 'pdp'
                : origin === 'sticky_bar'
                  ? isZoomModalOpen
                      ? 'sticky_bar_zoom'
                      : 'sticky_bar'
                  : 'pdp'

        void addItemToBasket([
            {
                productId: selectedVariant.ID,
                quantity: 1,
                price: selectedVariant.pricing?.price?.value,
            },
        ])
            .then(() => {
                // TODO change analytics product model
                const analyticsProductObject = getProductImpressionAnalytics({
                    product: product,
                    selectedVariantId: selectedVariant.ID,
                    flow: 'addToCart',
                    page: analyticsDimension22,
                    analyticsList: '',
                })
                const {list, ...analyticsProductWithoutList} = analyticsProductObject
                delete analyticsProductWithoutList.dimension14

                analyticsEventAddToCart({
                    currencyCode: basket?.currency || '',
                    list,
                    product: analyticsProductWithoutList,
                })
            })
            .finally(hideLoader)
    }

    const initializeSticky = (): IntersectionObserver => {
        const addToCartBtn = addToCartBtnRef.current
        const bannerAndAppointment = bannerAndAppointmentRef.current
        const footer = document.querySelector('footer[data-testid=footer]')

        // TODO handle margin top of collapsed menu for current breakpoint
        const visibility = {addToCart: false, footer: false}

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === addToCartBtn) {
                    visibility.addToCart = entry.isIntersecting
                } else if (entry.target === footer) {
                    visibility.footer = entry.isIntersecting
                } else if (entry.target === bannerAndAppointment) {
                    // Mobile "BUY" CTA position mode, derived from scroll position and banner/appointment section visibility:
                    //   - banner/appointment visible → 'hidden' (never overlap the banner/appointment)
                    //   - at the top              → 'inflow' (in flow above the color section)
                    //   - scrolled                → 'fixed'  (pinned to the bottom)
                    // Scroll-position driven (useScrollAtTop) so it pins as soon as the user scrolls — deliberately NOT
                    // modelled after the desktop stickybar (which only appears once the main buy button leaves the view).
                    if (entry.isIntersecting) {
                        setMobileCtaMode(() => 'hidden')
                    } else {
                        setMobileCtaMode(() => (isAtTopRef.current ? 'inflow' : 'fixed'))
                    }
                }
            })

            if (visibility.addToCart || visibility.footer) {
                stickybarRef.current?.classList.add('hidden')
            } else {
                stickybarRef.current?.classList.remove('hidden')
            }
        })

        if (addToCartBtn) {
            observer.observe(addToCartBtn)
        }

        if (footer) {
            observer.observe(footer)
        }

        if (bannerAndAppointment) {
            observer.observe(bannerAndAppointment)
        }

        return observer
    }

    const fireProductDetailAnalytics = (variationGroup: ProductVariationGroup) => {
        const hasDiscount = variationGroup.pricing?.hasDiscount ?? false
        const isSale = Boolean(product?.productPromotions?.some((promotion) => promotion.promotionId?.startsWith('sale')))
        const priceValue = hasDiscount ? variationGroup.pricing?.discount?.price?.value : variationGroup.pricing?.price?.value

        analyticsEventProductDetail({
            currencyCode: basket?.currency ?? '',
            dimension27: String(!!variationGroup.videos?.hasVerticalVideo || !!variationGroup.videos?.hasHorizontalVideo),
            product: {
                name: variationGroup.analytics?.shortDescription ?? '',
                id: variationGroup.analytics?.productId ?? variationGroup.ID,
                price: priceValue?.toFixed(2) ?? '',
                category: variationGroup.analytics?.categoryPath ?? '',
                // Analytics values must be in EN: use the analytics color (EN) computed by the
                // hook, not variationGroup.color which is the UI value localized in the user's language.
                variant: variationGroup.analytics?.color?.code ?? variationGroup.color?.code ?? '',
                color: variationGroup.analytics?.color?.label ?? '',
                gender: variationGroup.analytics?.gender ?? '',
                dimension14: String(variationGroup.analytics?.inStock ?? false),
                dimension26: String(isSale),
                // Analytics-only English callout from the c_pdp response; do not use textTag,
                // which remains the localized source for the PDP badge.
                callout: product.c_analytics?.callout ?? '',
            },
        })
    }

    // Tracks the variation-group signature for which productDetail was last fired, so pure
    // reference churn of `activeVariation` (e.g. after a size selection triggers a product
    // refetch) does not re-emit a spurious productDetail. It must fire once on landing and
    // again only on an actual color/variation-group change.
    const lastProductDetailKeyRef = useRef<string | null>(null)

    useEffect(() => {
        if (!activeVariation || !basket) return

        const variationKey = `${activeVariation.ID}|${activeVariation.analytics?.color?.code ?? ''}`
        if (lastProductDetailKeyRef.current === variationKey) return

        lastProductDetailKeyRef.current = variationKey
        fireProductDetailAnalytics(activeVariation)
    }, [activeVariation, basket])

    // Whenever the media set changes, snap the gallery back to the first image. This covers both a
    // color/variation change (the gallery is swapped) and a PDP→PDP SPA navigation (e.g. tapping a
    // recommendation): the organism stays mounted, so the vertical Swiper keeps its previous scroll
    // position unless we reset it — the page scrolls to top but the gallery would otherwise stay on the
    // last image. This must run on ALL viewports (the bug was mobile/tablet keeping the old position).
    //
    // The desktop-only images "gate" (which forces scrolling through every image before the page
    // unlocks) is a separate concern and stays gated to desktop.
    useEffect(() => {
        mediaCarouselRef.current?.resetToStart()
        if (!isDesktop) return
        setIsImagesGateOpen(false)
    }, [activeMediaItems, isDesktop])

    // Section-scoped wheel handler for desktop gating. Bound to the pinned images+details element (NOT
    // window), so it only fires for wheel events that happen over the PDP section itself. Overlays
    // (zoom modal, drawers, locale modal) are portaled to `document.body`, outside this element, so
    // their wheel events are never intercepted and scroll natively.
    // While the gate is CLOSED it consumes the wheel and drives the gallery, opening the gate the
    // moment the last image is reached (so it also unlocks when the final image is only partially
    // visible/cropped). When already at the first image and scrolling up, it lets the page scroll up
    // out of the PDP rather than trapping the user. When the gate is OPEN it does nothing (native page
    // scroll); the re-arm is handled by the scroll listener below.
    useEffect(() => {
        if (!isDesktop) return
        const el = imagesDetailsRef.current
        if (!el) return

        const handleWheel = (event: WheelEvent) => {
            if (!isGateActiveRef.current) return
            const carousel = mediaCarouselRef.current
            if (!carousel) return

            // Nothing to gate (e.g. a single short image): open immediately and let the page scroll.
            if (!carousel.isScrollable()) {
                setIsImagesGateOpen(true)
                return
            }

            const scrollingUp = event.deltaY < 0
            if (scrollingUp && carousel.isAtBeginning()) {
                // At the top of the gallery, scrolling up → allow the page to scroll up (unpin upward).

                // Expand header when scrolling up to the initial carousel position.
                setHeaderExpanded(true)

                return
            }

            if (!scrollingUp && carousel.isAtBeginning()) {
                // Collapse header when scrolling down from the initial carousel position.
                setHeaderExpanded(false)
            }

            event.preventDefault()
            carousel.driveBy(event.deltaY)
            if (carousel.isAtEnd()) {
                setIsImagesGateOpen(true)
            }
        }

        el.addEventListener('wheel', handleWheel, {passive: false})
        return () => el.removeEventListener('wheel', handleWheel)
    }, [isDesktop])

    // Re-arm the gate when the page returns to the very top, WITHOUT touching the gallery position:
    // the section pins again and further upward scrolling drives the images back through their frames
    // (per spec: scrolling back to the top restarts the behaviour). The gallery keeps its current
    // (last) frame so the transition into the pinned state is seamless.
    useEffect(() => {
        if (!isDesktop) return

        const handleScroll = () => {
            if (isImagesGateOpenRef.current && window.scrollY <= 0) {
                setIsImagesGateOpen(false)
            }
        }

        window.addEventListener('scroll', handleScroll, {passive: true})
        return () => window.removeEventListener('scroll', handleScroll)
    }, [isDesktop])

    useEffect(() => {
        if (mobileCtaMode !== 'hidden') {
            setMobileCtaMode(() => (isAtTop ? 'inflow' : 'fixed'))
        }
    }, [isAtTop])

    // Mobile-only, page-owned IntersectionObserver that tells the chat-positioning marker WHICH element
    // is the active sticky section. It watches a zero-size in-flow sentinel sitting at the bottom of the
    // white band (just above the color/size section). When that sentinel leaves the TOP of the viewport
    // the band has really started moving up — NOT the first pixel of scroll — so the page hands the
    // `cc-sticky-section` marker from the band to the fixed ADD TO CART CTA. Additive: it only updates a
    // local boolean, never `mobileCtaMode`. The handoff point is defined by the sentinel's own geometry,
    // so it is self-calibrating per product/viewport with no hardcoded pixel threshold.
    // Band-leaving signal (mobile only): on mobile the whole page scrolls together, so the white band
    // starts moving up the instant the page leaves scroll-top. We therefore hand the chat's sticky-section
    // marker from the band to the fixed CTA as soon as the page is scrolled (scrollY > 0), and give it back
    // to the band at scroll-top. Pure scroll read — no geometry, no state-machine change.
    useEffect(() => {
        if (isDesktop) return
        if (typeof window === 'undefined') return

        const update = () => setIsBandLeaving((window.scrollY || window.pageYOffset || 0) > 0)
        update() // sync with the current scroll position on mount / viewport change
        window.addEventListener('scroll', update, {passive: true})
        return () => window.removeEventListener('scroll', update)
    }, [isDesktop])

    const handleColorChange = (variationGroup: ProductVariationGroup) => {
        setSelectedVariant(null)
        onProductChange(variationGroup.ID)
    }

    const handleRelatedProductChange = (productId: string, productDescription: string) => {
        analyticsProductRelatedClick({productId, productDescription})
        setSelectedVariant(null)
        onProductChange(productId)
    }

    const openRequestSize = (sizeCode: string, variant: ProductVariant) => {
        openDrawer({
            title: (
                <ProductAttributeSelectorTitle
                    title={formatMessage({id: 'label.requestsize.subtitle', defaultMessage: 'Is the size you want not available?'})}
                />
            ),
            // The title renders an <h2>; keep the drawer header wrapper a <div> (default is <p>)
            // so we don't nest <h2> inside <p>. This also applies to the success title set later
            // via updateDrawerTitle, which inherits this titleAs.
            titleAs: Box,
            size: 'lg',
            variant: 'noBorder',
            closingIcon: 'IconChevronLeft',
            // Figma (node 5655-4320): PDP modal/drawer back (<) icon is 18px.
            closingIconSize: 'sm-plus',
            placement: 'right',
            body: (
                <ProductRequestSize
                    customer={customer}
                    productId={variant.ID}
                    variantsNotAvailable={activeVariation.variants.filter((v) => !v.selectable)}
                    step={1}
                    onSubmitForm={() => {
                        updateClosingIcon('IconClose', 'sm-plus')
                        updateDrawerTitle(
                            <ProductAttributeSelectorTitle
                                title={formatMessage({id: 'label.form.success.title', defaultMessage: 'Thank you for your request'})}
                            />,
                        )
                    }}
                />
            ),
            onOpen: () => {
                document.body.style.overflow = 'hidden'
            },
            onClose: () => {
                document.body.style.overflow = ''
            },
            onBack: () => {
                openSizeSelector(true)
            },
        })
    }

    const openSizeGuide = () => {
        // The full `pdp_size_guide_layer` lifecycle (open/switch/close, manuale BitBang PDP PWA §3.1.3) is
        // owned by the SizeGuide component itself, so the close fires on every dismissal path (back arrow,
        // bottom "Size Selector" CTA, overlay/ESC) — not just the drawer's onClose. Here we only keep the
        // legacy `sizeChart` event for SFRA parity.
        openDrawer({
            size: 'lg',
            variant: 'tabs',
            closingIcon: 'IconChevronLeft',
            // Figma (node 5655-4320): PDP modal/drawer back (<) icon is 18px.
            closingIconSize: 'sm-plus',
            placement: 'right',
            body: (
                <SizeGuide
                    sizeChartID={activeVariation?.sizeChartID ?? 'default'}
                    pageDesignerSizeGuideContent={pageContent['size_guide'] ?? []}
                    openSizeSelector={() => openSizeSelector(true)}
                    openHelpWithSizes={() => openContactUs({onBack: () => openSizeGuide(), hiddenFormValues: {infocategory: 'size', myquestion: 'product'}})}
                />
            ),
            onOpen: () => {
                document.body.style.overflow = 'hidden'
                analyticsEventSizeChartClick({category: 'Size Chart', action: 'open'})
            },
            onClose: () => {
                document.body.style.overflow = ''
                analyticsEventSizeChartClick({category: 'Size Chart', action: 'close'})
            },
            onBack: () => {
                openSizeSelector(true)
            },
        })
    }

    const openSizeSelector = (showNotice?: boolean) => {
        openDrawer({
            title: <ProductAttributeSelectorTitle title={LABEL_SIZE_SELECTOR_TITLE} />,
            titleAs: Box,
            size: 'lg',
            variant: 'noBorder',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'sm-plus',
            placement: 'right',
            body: (
                <ProductSizeSelector
                    hasSizeChart={activeVariation?.sizeChartID ? true : false}
                    variants={activeVariation?.variants}
                    selectedVariantId={selectedVariant?.ID}
                    notifyMeEnabled={activeVariation?.notifyMeEnabled}
                    showNotice={showNotice}
                    onSizeSelected={(sizeCode, variant) => {
                        // Mark that this drawer close is caused by a size selection, so the
                        // drawer's onClose does NOT also emit a spurious 'close' event.
                        // On SFRA a size selection emits only 'size select', never 'close'.
                        sizeSelectedRef.current = true
                        closeDrawer()
                        setSelectedVariant(() => variant)

                        analyticsProductSizeSelectorSizeSelect({productId: activeVariation?.ID, selectedSizeId: sizeCode})
                    }}
                    onRequestSize={(sizeCode, variant) => openRequestSize(sizeCode, variant)}
                    openSizeGuide={() => openSizeGuide()}
                    openHelpWithSizes={() =>
                        openContactUs({onBack: () => openSizeSelector(showNotice), hiddenFormValues: {infocategory: 'size', myquestion: 'product'}})
                    }
                />
            ),
            onOpen: () => {
                document.body.style.overflow = 'hidden'
                sizeSelectedRef.current = false
                analyticsProductSizeSelectorOpenClose({action: 'open', productId: activeVariation?.ID})
            },
            onClose: () => {
                document.body.style.overflow = ''

                // Only emit 'close' when the drawer is dismissed WITHOUT selecting a size.
                if (!sizeSelectedRef.current) {
                    analyticsProductSizeSelectorOpenClose({action: 'close', productId: activeVariation?.ID})
                }
                sizeSelectedRef.current = false

                // disable add to cart on size selection if drawer was closed
                setTimeout(() => {
                    if (shouldAddToCartAfterSizeSelection.current) {
                        shouldAddToCartAfterSizeSelection.current = false
                    }
                }, 0)
            },
        })
    }

    const openFindInBoutique = (findInBoutiqueVariant?: ProductVariant) => {
        const effectiveVariant = findInBoutiqueVariant || selectedVariant
        openDrawer({
            title: <FindInBoutiqueTitle selectSize={!effectiveVariant} />,
            titleAs: Box,
            body: effectiveVariant ? (
                <ProductFindInBoutique productVariant={effectiveVariant} productName={product?.c_shortDescription} productId={activeVariation.ID} />
            ) : (
                <ProductSizeSelector
                    variants={activeVariation?.variants}
                    showOnlySizes={true}
                    onSizeSelected={(sizeCode, variant) => {
                        analyticsProductFindInBoutiqueSizeSelection({productId: activeVariation.ID, selectedSizeLabel: variant.size.label})

                        closeDrawer()
                        openFindInBoutique(variant)
                    }}
                    onRequestSize={(sizeCode, variant) => openRequestSize(sizeCode, variant)}
                />
            ),
            placement: 'right',
            size: 'lg',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'sm-plus',
            variant: 'noBorder',
        })
    }

    const openColorSelector = () => {
        openDrawer({
            title: <ProductAttributeSelectorTitle title={LABEL_SELECT_COLOR} />,
            // The title renders an <h2>; keep the header wrapper a <div> (default is <p>) to avoid
            // nesting <h2> inside <p>.
            titleAs: Box,
            size: 'lg',
            variant: 'noBorder',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'sm-plus',
            placement: 'right',
            body: (
                <ProductColorSelector
                    variationGroups={product.c_variationGroupsData}
                    selectedColorCode={activeVariation?.color?.code}
                    onColorSelected={(colorCode, variationGroup) => {
                        // 'select color'/'click' fires ONLY when the color is changed from within the color
                        // selector drawer (Manuale BitBang PDP PWA §3.1.4) — not from inline swatches or the
                        // stickybar. Emit before closeDrawer so it precedes the layer 'close' event.
                        analyticsEventPdpColorLayerSelect()
                        handleColorChange(variationGroup)
                        closeDrawer()
                    }}
                />
            ),
            onOpen: () => {
                document.body.style.overflow = 'hidden'
                // Manuale BitBang PDP PWA §3.1.4.
                analyticsEventPdpColorLayer({action: 'open'})
            },
            onClose: () => {
                document.body.style.overflow = ''
                analyticsEventPdpColorLayer({action: 'close'})
            },
        })
    }

    const openContactUs = (options?: {onBack?: () => void; hiddenFormValues?: Record<string, string>}) => {
        openDrawer({
            title: <ContactUsTitle />,
            titleAs: Box,
            body: (
                <ContactUs
                    localeID={locale.alias}
                    productId={activeVariation.ID}
                    hiddenFormValues={options?.hiddenFormValues}
                    onSubmitForm={() => {
                        // Once submitted there is nothing to go back to, so the drawer just closes from here on
                        if (options?.onBack) updateClosingIcon('IconCloseLight', 'sm-plus')
                        updateDrawerTitle(
                            <ProductAttributeSelectorTitle
                                title={formatMessage({id: 'label.form.success.title', defaultMessage: 'Thank you for your request'})}
                            />,
                        )
                    }}
                />
            ),
            placement: 'right',
            size: 'lg',
            closingIcon: options?.onBack ? 'IconChevronLeft' : 'IconCloseLight',
            closingIconSize: 'sm-plus',
            variant: 'noBorder',
            onOpen: () => {
                document.body.style.overflow = 'hidden'
            },
            onClose: () => {
                document.body.style.overflow = ''
            },
            onBack: options?.onBack,
        })
    }

    // Fixed English analytics names for the product-info drawer tabs, aligned with the ProductInfo tabs
    // order (Descrizione / Confezione / Spedizione e Resi). Decoupled from the localized UI labels so the
    // analytics value is stable regardless of the user's language (manuale BitBang PDP PWA §3.1.2).
    const PRODUCT_INFO_TAB_ANALYTICS_NAMES = ['Description', 'Packaging', 'Shipping and returns']

    // Opens the Descrizione/Confezione/Spedizione e Resi drawer, pre-selecting the tab matching the clicked CTA.
    // Analytics (manuale BitBang PDP PWA §3.1.2): emits `pdp_detail_layer` open on display, switch on tab
    // change and close on dismiss, with the active tab name as eventAction. The active tab is tracked so the
    // close event reports the tab the user was last on, not the one the drawer opened with.
    const openProductInfo = (tabIndex: number) => {
        let activeTabName = PRODUCT_INFO_TAB_ANALYTICS_NAMES[tabIndex] ?? ''
        openDrawer({
            size: 'lg',
            variant: 'tabs',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'sm-plus',
            placement: 'right',
            body: (
                <ProductInfo
                    activeIndex={tabIndex}
                    product={product}
                    activeVariation={activeVariation}
                    pageContent={pageContent}
                    onTabChange={(index) => {
                        activeTabName = PRODUCT_INFO_TAB_ANALYTICS_NAMES[index] ?? ''
                        analyticsEventPdpDetailLayer({tabName: activeTabName, action: 'switch'})
                    }}
                />
            ),
            onOpen: () => {
                document.body.style.overflow = 'hidden'
                analyticsEventPdpDetailLayer({tabName: activeTabName, action: 'open'})
            },
            onClose: () => {
                document.body.style.overflow = ''
                analyticsEventPdpDetailLayer({tabName: activeTabName, action: 'close'})
            },
        })
    }

    // handle add to cart when a variant is selected
    useEffect(() => {
        if (selectedVariant && shouldAddToCartAfterSizeSelection.current) {
            handleAddToCart('pdp')
            shouldAddToCartAfterSizeSelection.current = false
        } else if (selectedVariant && shouldAddToCartAfterSizeSelectionSticky.current) {
            handleAddToCart('sticky_bar')
            shouldAddToCartAfterSizeSelectionSticky.current = false
        }
    }, [selectedVariant])

    // callback to set dwvar query parameter on size selected
    useEffect(() => {
        if (selectedVariant) {
            onVariantSelected()
        }
    }, [selectedVariant])

    // handle automatic add to cart
    const hasAutoAddedToCart = useRef(false)
    useEffect(() => {
        if (hasAutoAddedToCart.current) return

        const searchParams = new URLSearchParams(location.search)
        if (searchParams.get('addToCartAutomatic') === 'true' && selectedVariant && basket) {
            hasAutoAddedToCart.current = true
            handleAddToCart('pdp')
        }
    }, [basket])

    // Auto-open the Request Size (Notify Me) drawer when landing from the PLP tile "Request product" link.
    // Gated by the dedicated `requestSize=true` query param (set only by the tile SizeSelector) so it never
    // fires on ordinary variant deep-links that also carry color/size. The out-of-stock size is read from
    // the `size` param (SFRA `requestSizeFromPlp`, spec §16.16). Fire once, after variants are available.
    const hasOpenedRequestSizeFromPlp = useRef(false)
    useEffect(() => {
        if (hasOpenedRequestSizeFromPlp.current || !activeVariation?.variants) return

        const params = new URLSearchParams(location.search)
        if (params.get('requestSize') !== 'true') return

        const requestedSize = params.get('size')
        if (!requestedSize) return

        const requestedVariant = activeVariation.variants.find((v) => v.size?.code === requestedSize)
        // Only for a truly unavailable size (an available size would just be selectable, not "request").
        if (requestedVariant && !requestedVariant.availability?.inStock) {
            hasOpenedRequestSizeFromPlp.current = true
            openRequestSize(requestedSize, requestedVariant as unknown as ProductVariant)
        }
    }, [activeVariation])

    // initialize stickybar
    useEffect(() => {
        const intersectionObserver = initializeSticky()
        return () => intersectionObserver.disconnect()
    }, [])

    // Set correct size when activeVariation or product changes.
    // Will preselect an only size or reset the previously selected size.
    useEffect(() => {
        setSelectedVariant(() => getPreselectedVariant())
    }, [activeVariation])

    const shareProductPopoverContent = (
        <>
            {isActionLoading && <Loader />}

            <IconButton
                icon={<Icon name="IconHeart" size="pdp" isFilled={isInWishlist} />}
                size="iconButtonLg"
                isRounded={true}
                aria-label={addToWishlistAriaLabel}
                onClick={() => void handleAddToWishlist()}
                data-testid="pdp-add-to-wishlist-button"
            />

            <ShareBox
                isOpen={isShareProductPopoverOpen}
                onClose={onCloseShareProductPopover}
                shareUrl={shareUrl}
                shareSubject={formatMessage({
                    id: 'label.wishlist.share.email.subject',
                    defaultMessage: 'I would like to share this product with you',
                })}
            >
                <HStack as="button" {...styles.quickShareButton} onClick={onToggleShareProductPopover}>
                    <Icon name="IconShare" size="md" />
                </HStack>
            </ShareBox>
        </>
    )

    return (
        <VStack {...styles.container} className={`cc-pdp__container ${!isDesktop ? 'cc-mobile' : ''}`} data-testid="product-details">
            <HStack
                ref={imagesDetailsRef}
                {...styles.imagesAndDetailsContainer}
                {...(isGateActive ? styles.imagesAndDetailsContainerPinned : {})}
                className={`cc-pdp__images-details-container ${isGateActive ? 'cc-pdp__pinned' : ''}`}
                data-testid="pdp-images-details-container"
            >
                <Box {...styles.imagesContainer} className="cc-pdp__images" data-testid="pdp-images">
                    {productTextTag && (
                        <Box {...styles.productTag}>
                            <Tag variant="opacity">{productTextTag}</Tag>
                        </Box>
                    )}
                    <VStack {...styles.imagesWrapper}>
                        <ProductMediaCarousel
                            ref={mediaCarouselRef}
                            mediaItems={activeMediaItems}
                            productName={product?.c_shortDescription ?? ''}
                            onSlideClick={handleMediaSlideClick}
                        />
                    </VStack>
                </Box>
                {/* Desktop wishlist/share column. Rendered only on desktop: mobile and tablet show the
                    same controls inline next to the price (ctaButtonsMobileWrapper below). Both used to be
                    rendered and hidden via CSS `display`, but ShareBox portals its popover to the body, so
                    the hidden desktop copy still opened its Share popup on tablet — producing two popups
                    (both share the same isShareProductPopoverOpen state). Gating on isDesktop keeps a
                    single ShareBox instance per breakpoint. */}
                {isDesktop && (
                    <Flex className="cc-pdp__cta" {...styles.ctaButtonsWrapper}>
                        {shareProductPopoverContent}
                    </Flex>
                )}
                <Box {...styles.detailsContainer} className="cc-pdp__details" data-testid="pdp-details">
                    <HStack {...styles.detailsInnerRow}>
                        <VStack className={`cc-pdp__info ${!isDesktop && !isBandLeaving ? STICKY_SECTION_MARKER_CLASS : ''}`} sx={styles.detailsWrapper}>
                            <VStack {...styles.breadCrumbContainer}>
                                {breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} variant="pdp" />}
                            </VStack>
                            {!isDesktop ? (
                                <VStack {...styles.nameAndPriceContainer}>
                                    <Text as="h1" {...styles.detailsName}>
                                        {product?.c_shortDescription}
                                    </Text>
                                    <Flex {...styles.mobilePriceCtaRow}>
                                        <Flex {...styles.mobilePriceWrapper}>
                                            <Price
                                                variant="pdp"
                                                discountedPrice={discountedPrice}
                                                discountedPriceMinRange={discountedPriceMinRange}
                                                priceMinRange={priceMinRange}
                                            >
                                                {priceValue}
                                            </Price>
                                        </Flex>
                                        <HStack className="cc-pdp__cta" {...styles.ctaButtonsMobileWrapper}>
                                            {shareProductPopoverContent}
                                        </HStack>
                                    </Flex>
                                </VStack>
                            ) : (
                                <VStack {...styles.nameAndPriceContainer}>
                                    <Text as="h1" {...styles.detailsName}>
                                        {product?.c_shortDescription}
                                    </Text>
                                    <Price
                                        variant="pdp"
                                        discountedPrice={discountedPrice}
                                        discountedPriceMinRange={discountedPriceMinRange}
                                        priceMinRange={priceMinRange}
                                    >
                                        {priceValue}
                                    </Price>
                                </VStack>
                            )}

                            {/* Mobile & tablet "BUY" CTA. It starts IN FLOW here between name/price and the
                                color section (so it never overlaps the color/size rows and keeps name+price
                                above it), then becomes FIXED as soon as the user scrolls, and HIDES near
                                the footer (see mobileCtaMode). The slot reserves the CTA height while
                                'inflow' and collapses to 0 once the button leaves the flow, so the color
                                section does not keep a blank gap. On tablet (Figma node 60805-38152) the
                                same in-flow→sticky BUY sits between price and color and opens the mobile
                                confirmation layer — the desktop sticky bar is intentionally not used. */}
                            {!isDesktop && !isZoomModalOpen && !isSidebarOpen && (
                                <Box sx={mobileCtaMode === 'inflow' ? styles.mobileCtaSlot : styles.mobileCtaSlotCollapsed}>
                                    <StickyAddToCart
                                        activeVariation={activeVariation}
                                        selectedVariant={selectedVariant}
                                        productName={product?.shortDescription}
                                        productPrice={productFinalPrice}
                                        productPriceNumber={productPriceNumber}
                                        onAddToCart={(origin) => handleAddToCart(origin)}
                                        positionMode={mobileCtaMode}
                                        isActiveStickySection={!isDesktop ? isBandLeaving : true}
                                    />
                                </Box>
                            )}

                            <VStack {...styles.colorAndSizeContainer}>
                                <ProductColorSwatches
                                    variationGroups={product.c_variationGroupsData}
                                    selectedColorCode={activeVariation?.color?.code}
                                    onSwatchClick={(colorCode, variationGroup) => {
                                        handleColorChange(variationGroup)
                                    }}
                                    onMoreClick={openColorSelector}
                                />
                                <Divider />
                                <Button variant="changelocale" onClick={() => openSizeSelector()} sx={styles.sizeButtonInline}>
                                    <HStack {...styles.detailsSizeButtonWrapper}>
                                        <Text {...styles.detailsSizeButtonText}>
                                            {selectedVariant ? `${LABEL_SELECTED_SIZE} ${selectedVariant.size?.label}` : LABEL_SELECT_SIZE}
                                        </Text>
                                        <Icon name="IconChevronRight" size="xs" />
                                    </HStack>
                                </Button>
                                <Divider />
                            </VStack>

                            {isDesktop && (
                                <>
                                    <Button
                                        size="xs"
                                        variant="primary"
                                        sx={styles.addToCartButton}
                                        onClick={() => handleAddToCart('pdp')}
                                        ref={addToCartBtnRef}
                                    >
                                        {LABEL_ADD_TO_CART}
                                    </Button>
                                </>
                            )}

                            {product?.c_relatedProductsIds && (
                                <RelatedProductsSelector
                                    productIds={product.c_relatedProductsIds}
                                    activeProductId={activeVariation?.ID}
                                    onRelatedProductClick={handleRelatedProductChange}
                                />
                            )}

                            {shippingMessages?.length && (
                                <VStack {...styles.shippingMessagesContainer} data-testid="pdp-shipping-messages">
                                    {shippingMessages.map(
                                        ({content}, index) =>
                                            (content.links || content.text) && (
                                                <Flex {...styles.shippingMessageContainer} key={index}>
                                                    <Icon size="sm" name="IconInfo" color="#262626" sx={{flexShrink: 0}} />
                                                    <TextItem text={content.text} links={content.links} {...styles.shippingMessageAllert} />
                                                </Flex>
                                            ),
                                    )}
                                </VStack>
                            )}

                            {pageContent?.['taxes']?.[0] && (pageContent['taxes'][0].text || pageContent['taxes'][0].links) && (
                                // Duties/taxes disclaimer: same alert pattern (info icon + text) as the shipping
                                // messages, per Figma — all PDP disclaimers share the icon, alignment and font.
                                <Flex {...styles.shippingMessageContainer} data-testid="pdp-taxes-message">
                                    <Icon size="sm" name="IconInfo" color="#262626" sx={{flexShrink: 0}} />
                                    <TextItem text={pageContent['taxes'][0].text} links={pageContent['taxes'][0].links} {...styles.shippingMessageAllert} />
                                </Flex>
                            )}

                            <VStack {...styles.productInfoCtasContainer} data-testid="pdp-product-info-ctas">
                                <Button variant="changelocale" onClick={() => openProductInfo(0)} sx={styles.productInfoCtaButton}>
                                    <HStack {...styles.detailsSizeButtonWrapper}>
                                        <Text {...styles.detailsSizeButtonText}>{LABEL_PRODUCT_DETAILS}</Text>
                                        <Icon name="IconChevronRight" size="xs" />
                                    </HStack>
                                </Button>

                                <Button variant="changelocale" onClick={() => openProductInfo(1)} sx={styles.productInfoCtaButton}>
                                    <HStack {...styles.detailsSizeButtonWrapper}>
                                        <Text {...styles.detailsSizeButtonText}>{LABEL_PRODUCT_PACKAGING}</Text>
                                        <Icon name="IconChevronRight" size="xs" />
                                    </HStack>
                                </Button>

                                <Button variant="changelocale" onClick={() => openProductInfo(2)} sx={styles.productInfoCtaButton}>
                                    <HStack {...styles.detailsSizeButtonWrapper}>
                                        <Text {...styles.detailsSizeButtonText}>{LABEL_PRODUCT_SHIPPING_RETURNS}</Text>
                                        <Icon name="IconChevronRight" size="xs" />
                                    </HStack>
                                </Button>
                            </VStack>

                            <Stack {...styles.boutiqueAndHelpContainer}>
                                <Button size="xs" variant="underline" sx={styles.addToCartButton} onClick={() => openFindInBoutique()}>
                                    <HStack>
                                        <Icon name="IconMaps" />
                                        <Text>{LABEL_FIND_IN_BOUTIQUE}</Text>
                                    </HStack>
                                </Button>

                                <Button
                                    size="xs"
                                    variant="underline"
                                    sx={styles.addToCartButton}
                                    onClick={() => openContactUs({hiddenFormValues: {myquestion: 'product'}})}
                                >
                                    <HStack>
                                        <Icon name="IconEmail" />
                                        <Text>{LABEL_CONTACT_US}</Text>
                                    </HStack>
                                </Button>
                            </Stack>
                        </VStack>
                    </HStack>
                </Box>
            </HStack>

            {/* Desktop stickybar (desktop only). On mobile and tablet the "BUY" CTA is rendered in flow
                inside detailsWrapper (position: sticky) instead of here as a fixed element, so it no longer
                overlaps the color/size rows and the tablet uses the mobile confirmation layer rather than
                this bar (Figma node 60805-38152). */}

            {isDesktop && (
                <Box
                    sx={styles.stickybar}
                    // Additive style-free marker so the chat floats above the desktop sticky bar when it
                    // is shown. The existing `.hidden` toggling (stickybarRef.current.classList) only
                    // touches the 'hidden' token, leaving this marker intact; when '.hidden' is applied the
                    // bar is display:none so its zero-size rect is auto-ignored. styles.stickybar untouched.
                    className={`hidden ${STICKY_SECTION_MARKER_CLASS}`}
                    ref={stickybarRef}
                    data-testid="pdp-stickybar"
                >
                    <Stickybar
                        product={product}
                        priceValue={priceValue}
                        discountedPrice={discountedPrice}
                        discountedPriceMinRange={discountedPriceMinRange}
                        priceMinRange={priceMinRange}
                        activeVariation={activeVariation}
                        selectedVariant={selectedVariant}
                        onAddToCart={() => handleAddToCart('sticky_bar')}
                        onSizeSelected={(sizeCode, variant) => {
                            setSelectedVariant((prev) => variant)
                        }}
                        onColorSelected={(colorCode, variationGroup) => {
                            handleColorChange(variationGroup)
                        }}
                        shouldAddToCartAfterSizeSelection={shouldAddToCartAfterSizeSelectionSticky}
                        onRequestSize={(sizeCode, variant) => openRequestSize(sizeCode, variant)}
                        isZoomOpen={isZoomModalOpen}
                        // "Guida alle taglie" / "Possiamo aiutarti" from the sticky-bar size popover. When
                        // the zoom modal is open (the sticky bar is also shown over the zoom), first close
                        // the zoom so the drawer opens on the plain PDP — matching the behaviour the same
                        // CTA has from the closed-zoom size selector. closeModal() runs the zoom's onClose
                        // cleanup (resets body overflow + hides the sticky bar) via ModalContext.
                        openSizeGuide={() => {
                            if (isZoomModalOpen) closeModal()
                            openSizeGuide()
                        }}
                        openHelpWithSizes={() => {
                            if (isZoomModalOpen) closeModal()
                            openContactUs({onBack: () => openSizeSelector(false), hiddenFormValues: {infocategory: 'size', myquestion: 'product'}})
                        }}
                    />
                </Box>
            )}

            <Box w="100%">
                <ProductRecommendations product={product} />
            </Box>

            <VStack {...styles.bannerAndAppointmentContainer} ref={bannerAndAppointmentRef} data-testid="pdp-banner-appointment-section">
                {!!bannerPageRefs?.length && (
                    <Box {...styles.bannerContainer} data-testid="pdp-banner-section">
                        {bannerPageRefs.map((pageRef) => (
                            <BannerPage key={pageRef.pageId} pageRef={pageRef} />
                        ))}
                    </Box>
                )}

                {pageContent?.['appointment'] && (
                    <VStack {...styles.appointmentSection} data-testid="pdp-appointment-section">
                        {pageContent['appointment'].map((component, i) => {
                            if (component?.type === 'image') {
                                const {imageDesktop, imageTablet, imageMobile} = component
                                return <ImageItem imageDesktop={imageDesktop} imageTablet={imageTablet} imageMobile={imageMobile} key={i} />
                            }

                            if (component?.type === 'text') {
                                const {name: id, text, links}: {name: string; text: string; links: Array<{title: string; link: string}>} = component
                                const textStyle =
                                    id?.toLowerCase() === 'title'
                                        ? styles.appointmentTitle
                                        : id?.toLowerCase() === 'description'
                                          ? styles.appointmentDescription
                                          : undefined
                                return <TextItem styles={textStyle} text={text} links={links} key={i} />
                            }

                            if (component?.type === 'button') {
                                const {text, links} = component
                                return (
                                    <TextItem
                                        as={Button}
                                        {...styles.appointmentButton}
                                        text={text}
                                        links={links}
                                        onClick={() => analyticsEventExclusiveServicesClick({action: 'pdp', label: 'boutique-appointment'})}
                                        key={i}
                                    />
                                )
                            }
                        })}
                    </VStack>
                )}
            </VStack>
        </VStack>
    )
})

ProductDetails.displayName = 'ProductDetails'

export {ProductDetails}
